import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Newspaper, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const audienceLabel = (a) => ({ teachers: 'Teachers', parents: 'Parents', both: 'All' }[a] || a);
const audienceColor = (a) => ({
  teachers: 'bg-blue-100 text-blue-700 border-blue-300',
  parents: 'bg-purple-100 text-purple-700 border-purple-300',
  both: 'bg-emerald-100 text-emerald-700 border-emerald-300',
}[a] || 'bg-slate-100 text-slate-700 border-slate-300');

const NewsSection = ({ isAdmin = false }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', audience: 'both' });

  useEffect(() => {
    fetchNews();
    const handleKudosUpdated = () => fetchNews();
    window.addEventListener('kudos-updated', handleKudosUpdated);
    return () => window.removeEventListener('kudos-updated', handleKudosUpdated);
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const [newsRes, kudosRes] = await Promise.allSettled([
        axios.get(`${API}/api/news`, { withCredentials: true }),
        axios.get(`${API}/api/kudos`, { withCredentials: true }),
      ]);

      const newsData = newsRes.status === 'fulfilled' ? (newsRes.value.data || []) : [];
      const kudosData = kudosRes.status === 'fulfilled' ? (kudosRes.value.data || []) : [];

      // Map kudos to a simple news-like shape: title and content
      const kudosAsNews = kudosData.map((k) => ({
        id: `kudos-${k.id}`,
        title: `Kudos: ${k.user_name} → ${k.to_user_name || k.to_user_id}`,
        content: k.note || '',
        audience: 'both',
        author: k.user_name,
        created_at: k.created_at,
        _isKudos: true,
      }));

      // Combine and sort by created_at desc
      const combined = [...newsData, ...kudosAsNews].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNews(combined);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/news`, form, { withCredentials: true });
      toast.success('News published');
      setForm({ title: '', content: '', audience: 'both' });
      setDialogOpen(false);
      fetchNews();
    } catch (e) { toast.error('Failed to publish'); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this news post?')) return;
    try {
      await axios.delete(`${API}/api/news/${id}`, { withCredentials: true });
      toast.success('News deleted');
      fetchNews();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <Card className="bg-white border-slate-200" data-testid="news-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>News & Announcements</CardTitle>
          <CardDescription>{isAdmin ? 'Publish and manage announcements' : 'Latest updates from SSTC'}</CardDescription>
        </div>
        {isAdmin && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setDialogOpen(true)} data-testid="new-news-button">
            <Plus className="w-4 h-4 mr-2" />New Post
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {news.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Newspaper className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p>No announcements yet</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="news-list">
            {news.map((n, idx) => (
              <div key={n.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`news-item-${idx}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-900">{n.title}</h4>
                      <Badge className={`${audienceColor(n.audience)} border`}>{audienceLabel(n.audience)}</Badge>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-line">{n.content}</p>
                    <p className="text-xs text-slate-500 mt-2">By {n.author} • {new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                  {isAdmin && !n._isKudos && (
                    <Button variant="outline" size="sm" onClick={() => deletePost(n.id)} className="text-red-600" data-testid={`delete-news-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="news-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>New Announcement</DialogTitle>
            <DialogDescription>Choose the audience and post</DialogDescription>
          </DialogHeader>
          <form onSubmit={savePost} className="space-y-4" data-testid="news-form">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="news-title-input" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={5} data-testid="news-content-input" />
            </div>
            <div>
              <Label>Audience</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="news-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both" data-testid="news-audience-both">Everyone (Teachers &amp; Parents)</SelectItem>
                  <SelectItem value="teachers" data-testid="news-audience-teachers">Teachers Only</SelectItem>
                  <SelectItem value="parents" data-testid="news-audience-parents">Parents Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-news-button">Publish</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default NewsSection;
